<template>
                <div v-if="currentView === 'mail_audit_records'">
                    <div class="flex items-center justify-between mb-4">
                        <div class="page-header"><div><div class="page-title">進出郵件紀錄</div><div class="page-subtitle">查看所有進出站郵件的稽核紀錄</div></div></div>
                    </div>
                    <div class="card p-6">
                        <div class="flex flex-col gap-3 mb-4">
                            <div class="flex flex-wrap items-center gap-2">
                                <input v-model="mailAuditQuery" type="text" placeholder="搜尋：主旨 / 寄件者 / 收件者 / 狀態"
                                    class="input w-full md:w-80" @input="scheduleMailAuditSearch" />
                                <input v-model="mailAuditSince" type="date" class="input w-full md:w-44"
                                    @change="resetMailAuditAndFetch" />
                                <input v-model="mailAuditUntil" type="date" class="input w-full md:w-44"
                                    @change="resetMailAuditAndFetch" />
                                <select v-if="isSuperAdmin" v-model="mailAuditDomain" class="input w-full md:w-44"
                                    @change="resetMailAuditAndFetch">
                                    <option value="">全部網域</option>
                                    <option v-for="d in safeArr(domains)" :key="keyById(d, 'domain_opt')" :value="d.name">{{ d.name }}</option>
                                </select>
                                <select v-if="isDomainAdmin" v-model="mailAuditDirection" class="input w-full md:w-40"
                                    @change="resetMailAuditAndFetch">
                                    <option value="all">全部</option>
                                    <option value="in">進站</option>
                                    <option value="out">出站</option>
                                </select>
                                <input v-model.number="mailAuditMinScore" type="number" step="0.1" placeholder="最小分數"
                                    class="input w-full md:w-28" @change="resetMailAuditAndFetch" />
                                <input v-model.number="mailAuditMaxScore" type="number" step="0.1" placeholder="最大分數"
                                    class="input w-full md:w-28" @change="resetMailAuditAndFetch" />
                                <select v-model="mailAuditHasArchive" class="input w-full md:w-40"
                                    @change="resetMailAuditAndFetch">
                                    <option value="all">內容：全部</option>
                                    <option value="yes">內容：已保存</option>
                                    <option value="no">內容：未保存</option>
                                </select>
                                <select v-model.number="mailAuditLimit" class="input w-full md:w-32"
                                    @change="resetMailAuditAndFetch">
                                    <option :value="20">每頁 20</option>
                                    <option :value="50">每頁 50</option>
                                    <option :value="100">每頁 100</option>
                                </select>
                            </div>
                            <div class="flex flex-wrap items-center gap-2 justify-end">
                                <button type="button" class="btn btn-sm btn-outline" @click="resetMailAuditAndFetch" :disabled="mailAuditLoading">
                                    {{ mailAuditLoading ? '載入中...' : '重新整理' }}
                                </button>
                                <button type="button" class="btn btn-sm btn-outline" @click="exportMailAuditCsv" :disabled="mailAuditLoading || safeArr(mailAuditRecords).length === 0">
                                    匯出 CSV
                                </button>
                                <button type="button" class="btn btn-sm btn-outline" @click="exportMailAuditCsvAll" :disabled="mailAuditLoading || (mailAuditTotal || safeArr(mailAuditRecords).length) === 0">
                                    匯出全部
                                </button>
                                <button type="button" class="btn btn-sm btn-outline" @click="downloadMailAuditEmlSelectedZip"
                                    :disabled="mailAuditLoading || mailAuditBulkDownloadLoading || (mailAuditSelectedCount || 0) === 0">
                                    {{ mailAuditBulkDownloadLoading ? '下載中...' : `下載選取 EML (${mailAuditSelectedCount || 0})` }}
                                </button>
                                <button type="button" class="btn btn-sm btn-outline" @click="batchFetchMailAuditArchiveCurrentPage"
                                    :disabled="mailAuditLoading || mailAuditBatchArchiveLoading || safeArr(mailAuditRecords).filter(r => r && r.id && !r.has_archive).length === 0">
                                    {{ mailAuditBatchArchiveLoading ? `補抓 ${mailAuditBatchArchiveDone || 0}/${mailAuditBatchArchiveTotal || 0}` : '補抓本頁未保存' }}
                                </button>
                                <button type="button" class="btn btn-sm btn-outline" @click="batchFetchMailAuditArchiveAllFiltered"
                                    :disabled="mailAuditLoading || mailAuditBatchAllArchiveLoading || (mailAuditTotal || safeArr(mailAuditRecords).length) === 0">
                                    {{ mailAuditBatchAllArchiveLoading ? `補抓全部 ${mailAuditBatchAllArchiveDone || 0}/${mailAuditBatchAllArchiveTotal || 0}` : '補抓全部未保存' }}
                                </button>
                                <button type="button" class="btn btn-sm btn-outline" @click="mailAuditPrevPage" :disabled="mailAuditLoading || mailAuditSkip <= 0">
                                    上一頁
                                </button>
                                <button type="button" class="btn btn-sm btn-outline" @click="mailAuditNextPage"
                                    :disabled="mailAuditLoading || (mailAuditSkip + mailAuditLimit) >= (mailAuditTotal || 0)">
                                    下一頁
                                </button>
                            </div>
                        </div>
                        <div v-if="mailAuditError" class="text-sm mb-3" style="color:var(--danger);">{{ mailAuditError }}</div>
                        <div class="text-xs mb-3" style="color:var(--gray-500);">
                            共 {{ mailAuditTotal || safeArr(mailAuditRecords).length }} 筆
                            <span v-if="mailAuditTotal">，第 {{ Math.floor((mailAuditSkip || 0) / (mailAuditLimit || 1)) + 1 }} / {{ Math.max(1, Math.ceil((mailAuditTotal || 0) / (mailAuditLimit || 1))) }} 頁</span>
                        </div>
                        <table class="data-table">
                            <thead>
                                <tr class="border-b" style="background:var(--gray-50);">
                                    <th class="p-2 w-8">
                                        <input type="checkbox" :checked="mailAuditAllSelectedOnPage" @change="toggleMailAuditSelectAllCurrentPage" />
                                    </th>
                                    <th v-if="isSuperAdmin" class="p-2">網域</th>
                                    <th class="p-2">日期時間</th>
                                    <th class="p-2">主旨</th>
                                    <th class="p-2">寄件者</th>
                                    <th class="p-2">收件者</th>
                                    <th class="p-2">垃圾郵件分數</th>
                                    <th class="p-2">狀態</th>
                                    <th class="p-2">方向</th>
                                    <th class="p-2">內容</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, idx) in safeArr(mailAuditRecords)" :key="keyById(row, 'mail_audit', idx)" class="border-b">
                                    <td class="p-2 w-8">
                                        <input type="checkbox" :checked="mailAuditSelectedIds && mailAuditSelectedIds[Number(row?.id) || 0] === true"
                                            @change="toggleMailAuditSelected(row, $event)" />
                                    </td>
                                    <td v-if="isSuperAdmin" class="p-2">{{ mailAuditDomainLabel(row) }}</td>
                                    <td class="p-2">{{ row.timestamp ? new Date(row.timestamp).toLocaleString() : '-' }}</td>
                                    <td class="p-2">
                                        <div class="flex items-center gap-2">
                                            <span>{{ row.subject || '(無主旨)' }}</span>
                                            <button v-if="row.id" type="button" class="text-xs hover:underline" style="color:var(--primary);"
                                                @click.stop="openMailAuditDetail(row)">查看</button>
                                        </div>
                                    </td>
                                    <td class="p-2">{{ row.sender || '-' }}</td>
                                    <td class="p-2">{{ row.recipient || '-' }}</td>
                                    <td class="p-2">{{ (row.spam_score ?? '-') }}</td>
                                    <td class="p-2">
                                        <span v-if="row.status" class="px-2 py-0.5 rounded text-xs" :class="mailAuditStatusClass(row.status)">{{ row.status }}</span>
                                        <span v-else style="color:var(--gray-400);">-</span>
                                    </td>
                                    <td class="p-2">
                                        <span v-if="row.direction === 'in'" class="text-xs" style="color:var(--primary);">進站</span>
                                        <span v-else-if="row.direction === 'out'" class="text-xs" style="color:var(--info,#7c3aed);">出站</span>
                                        <span v-else style="color:var(--gray-400);">-</span>
                                    </td>
                                    <td class="p-2">
                                        <span v-if="row.has_archive" class="badge badge-success">已保存</span>
                                        <span v-else class="px-2 py-0.5 rounded text-xs" style="color:var(--gray-700);background:var(--gray-200);">未保存</span>
                                    </td>
                                </tr>
                                <tr v-if="!mailAuditLoading && safeArr(mailAuditRecords).length === 0">
                                    <td :colspan="isSuperAdmin ? 10 : 9" class="p-4 text-center" style="color:var(--gray-500);">找不到紀錄。</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>


</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  mailAuditQuery, mailAuditDirection, mailAuditSince, mailAuditUntil,
  mailAuditDomain, mailAuditHasArchive,
  isSuperAdmin, isDomainAdmin,
  mailAuditLoading, mailAuditError, mailAuditTotal, mailAuditSkip, mailAuditLimit,
  mailAuditMinScore, mailAuditMaxScore,
  mailAuditSelectedIds, mailAuditBulkDownloadLoading,
  mailAuditBatchArchiveLoading, mailAuditBatchArchiveDone, mailAuditBatchArchiveTotal,
  mailAuditBatchAllArchiveLoading, mailAuditBatchAllArchiveDone, mailAuditBatchAllArchiveTotal,
  mailAuditSelectedCount, mailAuditAllSelectedOnPage,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  exportMailAuditCsv, exportMailAuditCsvAll, resetMailAuditAndFetch,
  mailAuditPrevPage, mailAuditNextPage, downloadMailAuditEmlSelectedZip,
  batchFetchMailAuditArchiveCurrentPage, batchFetchMailAuditArchiveAllFiltered,
  mailAuditStatusClass, mailAuditDomainLabel, scheduleMailAuditSearch,
  toggleMailAuditSelectAllCurrentPage, toggleMailAuditSelected, openMailAuditDetail,
} = store
</script>

